export const KOOB_DEFAULT_THEME = {
  primaryColor: '#F67008',
  inkColor: '#1E293B',
  paperColor: '#FCF9F2',
  displayFont: 'Cormorant Garamond',
  bodyFont: 'Karla',
};

const DISPLAY_FONT_WEIGHTS = [400, 600];
const BODY_FONT_WEIGHTS = [400, 500, 700];

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const FONT_FAMILY_REGEX = /^[A-Za-z0-9 -]{1,100}$/;

function hexToRgb(hex) {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function rgbToHex([r, g, b]) {
  return `#${[r, g, b]
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

function mix(from, to, weight) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return rgbToHex(
    a.map((channel, i) => channel * weight + b[i] * (1 - weight)),
  );
}

function foregroundFor(hex) {
  const [r, g, b] = hexToRgb(hex);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 140 ? '#FFFFFF' : '#111111';
}

function resolveField(value, fallback, pattern) {
  return typeof value === 'string' && pattern.test(value) ? value : fallback;
}

export function resolveTravelDiaryTheme(theme = {}) {
  const primaryColor = resolveField(
    theme?.primaryColor,
    KOOB_DEFAULT_THEME.primaryColor,
    HEX_COLOR_REGEX,
  );
  const inkColor = resolveField(
    theme?.inkColor,
    KOOB_DEFAULT_THEME.inkColor,
    HEX_COLOR_REGEX,
  );
  const paperColor = resolveField(
    theme?.paperColor,
    KOOB_DEFAULT_THEME.paperColor,
    HEX_COLOR_REGEX,
  );
  const displayFont = resolveField(
    theme?.displayFont,
    KOOB_DEFAULT_THEME.displayFont,
    FONT_FAMILY_REGEX,
  );
  const bodyFont = resolveField(
    theme?.bodyFont,
    KOOB_DEFAULT_THEME.bodyFont,
    FONT_FAMILY_REGEX,
  );

  return { primaryColor, inkColor, paperColor, displayFont, bodyFont };
}

// Colors recomputed from the 3 basetokens every time one of them changes.
export function deriveTravelDiaryColors(theme) {
  return {
    primarySoft: mix(theme.primaryColor, theme.paperColor, 0.12),
    primaryFaint: mix(theme.primaryColor, theme.paperColor, 0.05),
    primaryStrong: mix(theme.primaryColor, '#000000', 0.85),
    inkMuted: mix(theme.inkColor, theme.paperColor, 0.45),
    inkSoft: mix(theme.inkColor, theme.paperColor, 0.75),
    paperSecondary: mix(theme.paperColor, theme.inkColor, 0.97),
    onPrimary: foregroundFor(theme.primaryColor),
    onInk: foregroundFor(theme.inkColor),
  };
}

export function buildGoogleFontsUrl(theme) {
  const families = [
    [theme.displayFont, DISPLAY_FONT_WEIGHTS],
    [theme.bodyFont, BODY_FONT_WEIGHTS],
  ]
    .map(
      ([family, weights]) =>
        `family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@${weights.join(';')}`,
    )
    .join('&');

  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

export function toCssVariables(theme) {
  const derived = deriveTravelDiaryColors(theme);
  return {
    '--td-primary': theme.primaryColor,
    '--td-ink': theme.inkColor,
    '--td-paper': theme.paperColor,
    '--td-primary-soft': derived.primarySoft,
    '--td-primary-faint': derived.primaryFaint,
    '--td-primary-strong': derived.primaryStrong,
    '--td-ink-muted': derived.inkMuted,
    '--td-ink-soft': derived.inkSoft,
    '--td-paper-secondary': derived.paperSecondary,
    '--td-on-primary': derived.onPrimary,
    '--td-on-ink': derived.onInk,
    '--td-font-display': `"${theme.displayFont}", "Cormorant Garamond", serif`,
    '--td-font-body': `"${theme.bodyFont}", "Karla", sans-serif`,
  };
}
